import { env } from '../env';
import { AppConfig } from './types';
import { validateConfig } from './validate';

export class ConfigFetchError extends Error {
  readonly detail?: string;
  constructor(message: string, detail?: string) {
    super(message);
    this.name = 'ConfigFetchError';
    this.detail = detail;
  }
}

const DEFAULT_RETRIES = 3;
const DEFAULT_TIMEOUT_MS = 8000;
const DEFAULT_BASE_DELAY_MS = 300;
const MAX_DETAIL_LENGTH = 500;

function isRetryableStatus(status: number): boolean {
  return status === 429 || (status >= 500 && status <= 599);
}

async function readErrorDetail(res: Response): Promise<string | undefined> {
  try {
    const text = await res.text();
    if (!text) return undefined;
    return text.length > MAX_DETAIL_LENGTH ? text.slice(0, MAX_DETAIL_LENGTH) + '…' : text;
  } catch {
    return undefined;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function loadConfig(): Promise<AppConfig> {
  const base = env.API_BASE_URL;
  if (!base) {
    throw new ConfigFetchError('API_BASE_URL is not set in window.env (/env.js).');
  }
  const url = `${String(base).replace(/\/+$/, '')}/app-config/student`;

  let lastDetail: string | undefined;
  for (let attempt = 0; attempt <= DEFAULT_RETRIES; attempt++) {
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
      });
    } catch (err) {
      if (attempt < DEFAULT_RETRIES) {
        await delay(DEFAULT_BASE_DELAY_MS * 2 ** attempt + Math.random() * DEFAULT_BASE_DELAY_MS);
        continue;
      }
      throw new ConfigFetchError(`Could not reach ${url}: ${String(err)}`, lastDetail);
    }

    if (!res.ok) {
      lastDetail = await readErrorDetail(res);
      if (isRetryableStatus(res.status) && attempt < DEFAULT_RETRIES) {
        await delay(DEFAULT_BASE_DELAY_MS * 2 ** attempt + Math.random() * DEFAULT_BASE_DELAY_MS);
        continue;
      }
      throw new ConfigFetchError(`GET ${url} returned ${res.status}.`, lastDetail);
    }

    let text: string;
    try {
      text = await res.text();
    } catch (err) {
      // A timeout/abort firing during body streaming is transient — retry it
      // like a network error rather than misreporting it as bad JSON.
      if (attempt < DEFAULT_RETRIES) {
        await delay(DEFAULT_BASE_DELAY_MS * 2 ** attempt + Math.random() * DEFAULT_BASE_DELAY_MS);
        continue;
      }
      throw new ConfigFetchError(`Could not reach ${url}: ${String(err)}`, lastDetail);
    }

    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      // 200 with a non-JSON body (misrouted URL / proxy error page) — not retryable.
      throw new ConfigFetchError(
        `GET ${url} returned a ${res.status} that is not valid JSON (likely a misrouted URL).`,
        text.length > MAX_DETAIL_LENGTH ? text.slice(0, MAX_DETAIL_LENGTH) + '…' : text
      );
    }

    // validateConfig throws ConfigValidationError, which propagates un-retried.
    return validateConfig(body);
  }
  throw new ConfigFetchError(`Could not load ${url}.`, lastDetail);
}
